
import { CustomCanvas } from "@/types/fabric";
import { Canvas } from "fabric";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface VersionControlResult {
  success: boolean;
  message?: string;
  version?: number;
  conflictType?: 'minor' | 'major';
}

export const useVersionControl = () => {
  const checkVersionConflict = async (drawingId: string, currentVersion: number): Promise<VersionControlResult> => {
    try {
      if (!drawingId) {
        return {
          success: false,
          message: "No drawing ID provided for version check.",
        };
      }

      console.log("Checking version conflict:", { drawingId, currentVersion });
      
      const { data: currentDrawing, error: fetchError } = await supabase
        .from("whiteboard_history")
        .select('version')
        .eq('id', drawingId)
        .single();

      if (fetchError) {
        console.error("Version fetch error:", fetchError);
        return {
          success: false,
          message: "Unable to verify drawing version.",
        };
      }

      if (!currentDrawing) {
        console.error("Drawing not found:", drawingId);
        return {
          success: false,
          message: "Drawing not found. It may have been deleted.",
        };
      }

      if (currentDrawing.version !== currentVersion) {
        console.warn("Version conflict detected:", { 
          currentVersion, 
          serverVersion: currentDrawing.version 
        });
        
        // Determine if it's a minor or major conflict
        const versionDiff = currentDrawing.version - currentVersion;
        const conflictType = versionDiff <= 1 ? 'minor' : 'major';
        
        return {
          success: false,
          message: "This drawing has been modified elsewhere. Please reload the latest version.",
          version: currentDrawing.version,
          conflictType
        };
      }

      return { success: true, version: currentDrawing.version };
    } catch (error) {
      console.error("Version check error:", error);
      return {
        success: false,
        message: "Unable to verify drawing version due to a network error.",
      };
    }
  };

  const updateCanvasVersion = (canvas: Canvas | null, drawingId: string, version: number) => {
    if (!canvas) return;

    try {
      console.log("Updating canvas version:", { drawingId, version });
      
      (canvas as CustomCanvas).data = {
        ...(canvas as CustomCanvas).data,
        currentDrawingId: drawingId,
        lastModified: new Date().toISOString(),
        version,
        isModified: false
      };
    } catch (error) {
      console.error("Error updating canvas version:", error);
      // This is non-critical, we'll continue without throwing
    }
  };

  const getCanvasVersion = (canvas: Canvas | null): number => {
    if (!canvas) return 1;
    
    try {
      const version = (canvas as CustomCanvas).data?.version;
      return typeof version === 'number' ? version : 1;
    } catch (error) {
      console.error("Error getting canvas version:", error);
      return 1;
    }
  };

  const markCanvasAsModified = (canvas: Canvas | null) => {
    if (!canvas) return;
    
    try {
      (canvas as CustomCanvas).data = {
        ...(canvas as CustomCanvas).data,
        isModified: true
      };
    } catch (error) {
      console.error("Error marking canvas as modified:", error);
    }
  };

  const trackDrawingLineage = (canvas: Canvas | null, originalDrawingId: string | null) => {
    if (!canvas || !originalDrawingId) return;
    
    try {
      // Fixed: Using proper type cast and checking if data exists
      const customCanvas = canvas as CustomCanvas;
      if (customCanvas.data) {
        customCanvas.data = {
          ...customCanvas.data,
          originalDrawingId
        };
      } else {
        customCanvas.data = { originalDrawingId };
      }
      console.log("Tracked drawing lineage:", { originalDrawingId });
    } catch (error) {
      console.error("Error tracking drawing lineage:", error);
    }
  };

  return {
    checkVersionConflict,
    updateCanvasVersion,
    getCanvasVersion,
    markCanvasAsModified,
    trackDrawingLineage
  };
};
